import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'chat-widget-width';
const MIN_WIDTH = 480;
const MAX_WIDTH = 800;

function readStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
  } catch {
    // localStorage unavailable
  }
  return MIN_WIDTH;
}

interface UseWidthResizeResult {
  width: number;
  widthHandleRef: React.RefObject<HTMLDivElement>;
}

export function useWidthResize(): UseWidthResizeResult {
  const [width, setWidth] = useState(readStoredWidth);
  const widthHandleRef = useRef<HTMLDivElement>(null!);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingRef.current) {
      return;
    }
    const delta = startXRef.current - e.clientX;
    const newWidth = Math.min(
      MAX_WIDTH,
      Math.max(MIN_WIDTH, startWidthRef.current + delta),
    );
    setWidth(newWidth);
  }, []);

  const onMouseUp = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const handle = widthHandleRef.current;
    if (!handle) {
      return;
    }

    const onMouseDown = (e: MouseEvent) => {
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    handle.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      handle.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [width, onMouseMove, onMouseUp]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(width)));
    } catch {
      // localStorage unavailable
    }
  }, [width]);

  return { width, widthHandleRef };
}
